import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaInstagram, FaTelegramPlane, FaTiktok, FaWhatsapp } from "react-icons/fa";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";

const PAYMENT_ADDRESS = "TYGre7SuTUeT6XYw68W9tiPVWFAAjPhLAB";
const PAYMENT_CONTACT_URL = "https://t.me/Profitmakertool";
const PAYMENT_CONTACT_LABEL = "t.me/Profitmakertool";

const socialLinks = [
  { label: "Telegram", href: "https://t.me/matchespro", icon: FaTelegramPlane },
  {
    label: "TikTok",
    href: "https://www.tiktok.com/@profitmakertool?_r=1&_t=ZS-95oLSw4DVxo",
    icon: FaTiktok,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/markethunter.site?igsh=MWx0NXNxa3ljOXZzaA%3D%3D&utm_source=qr",
    icon: FaInstagram,
  },
  {
    label: "WhatsApp",
    href: "https://chat.whatsapp.com/BiM2rqbgAzJ1yBOE5h6E8p",
    icon: FaWhatsapp,
  },
];

const PRODUCTS = [
  {
    id: "overUnder",
    name: "Over/Under Pro",
    price: 70,
    description: "Signal whether the last digit will be OVER (5–9) or UNDER (0–4)",
    href: "/over-under",
    color: "#819C97",
  },
  {
    id: "evenOdd",
    name: "Even/Odd Pro",
    price: 100,
    description: "Signal whether the last digit will be EVEN or ODD",
    href: "/even-odd",
    color: "#819C97",
  },
  {
    id: "matches",
    name: "Matches Pro",
    price: 200,
    description: "Predict the exact last digit for Matches contracts",
    href: "/user",
    color: "#A87F23",
  },
];

export default function PendingApproval() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlUid = params.get("uid");

    if (urlUid) {
      localStorage.setItem("userUid", urlUid);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const verifyAccess = async () => {
      const loginid = localStorage.getItem("currentDerivLogin");

      if (!loginid) {
        navigate("/login", { replace: true });
        return;
      }

      try {
        const usersCol = collection(db, "deriv_users");
        const q = query(usersCol, where("loginid", "==", loginid));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          localStorage.removeItem("currentDerivLogin");
          navigate("/login", { replace: true });
          return;
        }

        const data = querySnapshot.docs[0].data();

        if (data.approved) {
          navigate("/user", { replace: true });
          return;
        }

        setUserData(data);
        setAuthorized(true);
      } catch (err) {
        console.error("[PendingApproval] Auth check failed:", err);
        localStorage.removeItem("currentDerivLogin");
        navigate("/login", { replace: true });
      } finally {
        setLoading(false);
      }
    };

    verifyAccess();
  }, [navigate]);

  const isApproved = (productId) => {
    if (!userData) return false;
    if (productId === "matches") return userData.approved || userData.approvals?.matches;
    return userData.approvals?.[productId] === true;
  };

  const approvedProducts = PRODUCTS.filter((p) => isApproved(p.id));

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0f1514] text-slate-300">
        Verifying your Matches Pro access...
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0f1514] px-6 text-white">
        <div className="max-w-md rounded-[2rem] border border-white/10 bg-[#17201f] p-8 text-center">
          <h2 className="text-2xl font-black text-[#A87F23]">Access restricted</h2>
          <p className="mt-4 leading-7 text-slate-300">This area is only available to signed-in Matches Pro users.</p>
          <Link
            to="/login"
            className="mt-8 inline-flex rounded-full bg-[#A87F23] px-6 py-3 font-semibold text-[#0f1514] transition hover:bg-[#c19432]"
          >
            Return to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f1514] text-white">
      <div className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.jpeg" alt="Matches Pro" className="h-10 w-10 rounded-full border border-white/10 object-cover" />
            <div>
              <p className="text-lg font-black tracking-[0.15em] text-[#A87F23]">MATCHES PRO</p>
              <p className="text-xs uppercase tracking-[0.28em] text-[#819C97]">Payment and approval</p>
            </div>
          </Link>
          <Link to="/" className="text-sm font-semibold text-[#819C97] transition hover:text-[#A87F23]">
            Back to home
          </Link>
        </div>

        <div className="mt-12 grid gap-8 lg:grid-cols-[1.1fr_0.9fr]">
          <section className="space-y-8">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.28em] text-[#819C97]">Pending approval</p>
              <h1 className="mt-3 text-4xl font-black leading-tight">
                Your Deriv account is connected. Complete payment to unlock your tools.
              </h1>
              <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-300">
                Pay for one or more tools below, then contact{" "}
                <a
                  href={PAYMENT_CONTACT_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold text-[#A87F23] transition hover:text-[#d4a548]"
                >
                  {PAYMENT_CONTACT_LABEL}
                </a>{" "}
                for confirmation.
              </p>
            </div>

            {/* Approved products — show access links */}
            {approvedProducts.length > 0 && (
              <div className="rounded-[2rem] border border-[#819C97]/30 bg-[#819C97]/10 p-6">
                <p className="text-sm font-semibold uppercase tracking-[0.28em] text-[#819C97]">
                  Your active tools
                </p>
                <div className="mt-4 flex flex-col gap-3">
                  {approvedProducts.map((p) => (
                    <Link
                      key={p.id}
                      to={p.href}
                      className="flex items-center justify-between rounded-2xl border border-[#819C97]/30 bg-[#17201f] px-5 py-4 transition hover:border-[#A87F23]/50"
                    >
                      <div>
                        <p className="font-bold text-white">{p.name}</p>
                        <p className="text-sm text-slate-400">{p.description}</p>
                      </div>
                      <span className="ml-4 shrink-0 rounded-full bg-[#819C97] px-3 py-1 text-xs font-bold text-[#0f1514]">
                        OPEN →
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Three product cards */}
            <div>
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.28em] text-[#819C97]">
                Available tools
              </p>
              <div className="space-y-4">
                {PRODUCTS.map((p) => {
                  const approved = isApproved(p.id);
                  return (
                    <div
                      key={p.id}
                      className={`rounded-[1.75rem] border p-6 ${
                        approved
                          ? "border-[#819C97]/30 bg-[#819C97]/8"
                          : "border-white/10 bg-white/5"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-3">
                            <p className="text-xl font-black text-white">{p.name}</p>
                            {approved && (
                              <span className="rounded-full bg-[#819C97]/20 px-2.5 py-0.5 text-xs font-medium text-[#a8c4bf]">
                                Approved
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-sm text-slate-400">{p.description}</p>
                        </div>
                        <p className="shrink-0 text-2xl font-black text-[#A87F23]">${p.price}</p>
                      </div>

                      {approved ? (
                        <Link
                          to={p.href}
                          className="mt-4 inline-flex rounded-full bg-[#819C97] px-5 py-2 text-sm font-semibold text-[#0f1514] transition hover:brightness-110"
                        >
                          Open dashboard →
                        </Link>
                      ) : (
                        <p className="mt-3 text-sm text-slate-500">
                          Pay ${p.price} USDT TRC20 and contact Telegram to activate.
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-[2rem] border border-white/10 bg-white/5 p-7">
              <p className="text-sm font-semibold uppercase tracking-[0.28em] text-[#819C97]">Official channels</p>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {socialLinks.map(({ label, href, icon: Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#17201f] px-4 py-3 transition hover:border-[#A87F23]/50"
                  >
                    <Icon className="text-[#819C97]" />
                    <span>{label}</span>
                  </a>
                ))}
              </div>
            </div>
          </section>

          <aside className="lg:sticky lg:top-8 lg:self-start">
            <div className="rounded-[2rem] border border-[#A87F23]/30 bg-[#17201f] p-8 shadow-2xl shadow-black/25">
              <p className="text-sm font-semibold uppercase tracking-[0.28em] text-[#819C97]">Payment details</p>

              <div className="mt-6 space-y-3">
                {PRODUCTS.map((p) => (
                  <div
                    key={p.id}
                    className={`flex items-center justify-between rounded-2xl border px-4 py-3 ${
                      isApproved(p.id)
                        ? "border-[#819C97]/30 bg-[#819C97]/10"
                        : "border-white/10 bg-white/5"
                    }`}
                  >
                    <span className="text-sm text-slate-300">{p.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="font-black text-[#A87F23]">${p.price}</span>
                      {isApproved(p.id) && (
                        <span className="text-xs text-[#819C97]">✓</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-[1.5rem] border border-white/10 bg-[#0f1514] p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-slate-400">USDT TRC20 address</p>
                <p className="mt-3 break-all font-mono text-sm text-[#819C97]">{PAYMENT_ADDRESS}</p>
              </div>

              <p className="mt-5 text-sm leading-6 text-slate-400">
                Send the amount for your chosen tool(s) to this wallet, then message Telegram at {PAYMENT_CONTACT_LABEL} so the payment can be confirmed.
              </p>

              <a
                href={PAYMENT_CONTACT_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-8 block rounded-full bg-[#A87F23] py-4 text-center font-bold text-[#0f1514] transition hover:bg-[#c19432]"
              >
                Contact on Telegram after payment
              </a>

              <div className="mt-6 rounded-[1.5rem] border border-white/10 bg-white/5 p-5">
                <p className="text-sm font-semibold text-white">Need help first?</p>
                <div className="mt-4 grid gap-3">
                  {socialLinks.map(({ label, href }) => (
                    <a key={label} href={href} target="_blank" rel="noreferrer" className="text-sm text-[#819C97] transition hover:text-[#A87F23]">
                      {label}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <footer className="border-t border-white/10 py-8 text-center text-sm text-slate-500">
        Copyright 2026 Matches Pro. Trading carries risk and requires personal judgment.
      </footer>
    </div>
  );
}
